import { useState } from 'react';

const features = [
  {
    icon: 'ri-flashlight-fill',
    title: '智能秒播技术',
    desc: '自主研发的智能预加载与自适应码率引擎，根据您的网络环境实时优化播放策略，实现点击即播零等待。4G 网络下平均加载时间仅 0.8 秒，告别缓冲圈。',
  },
  {
    icon: 'ri-4k-fill',
    title: '全高清片源库',
    desc: '全网收录 1080P 及以上高清片源，支持 HDR 与杜比音效。片库总量突破 20000+ 部，涵盖 30 余种语言版本，每日持续扩充更新，满足不同类型影迷的需求。',
  },
  {
    icon: 'ri-shield-check-fill',
    title: '安全稳定可靠',
    desc: '采用企业级 CDN 加速与多节点备份架构，全球范围内提供稳定流畅的观影服务。全站 HTTPS 加密传输，严格保护您的隐私数据，让观影安心无忧。',
  },
];

const faqs = [
  {
    q: '午马影院是否需要注册才能观看？',
    a: '不需要。午马影院所有电影均可免费在线观看，无需注册账号或登录，打开即看，方便快捷。',
  },
  {
    q: '电影画质如何？需要缓冲吗？',
    a: '大部分影片支持 1080P 全高清画质，部分新片提供 4K HDR 版本。采用智能秒播技术，正常情况下点击即可播放，几乎无需等待缓冲。',
  },
  {
    q: '支持哪些设备观看？',
    a: '午马影院全面适配 PC、平板、手机等设备，支持 Chrome、Safari、Edge 等主流浏览器。移动端可通过浏览器直接访问，也提供 Android 与 iOS App 下载。',
  },
  {
    q: '影片更新频率如何？',
    a: '我们每日同步更新最新院线下线影片与网络新片，平均每日新增 30+ 部电影资源。热门大片通常在上映后 1-2 个月内即可观看。',
  },
  {
    q: '可以下载电影离线观看吗？',
    a: '移动端 App 支持离线缓存功能，可将影片下载到本地设备，方便在无网络环境下观看。网页版暂不支持下载功能。',
  },
  {
    q: '遇到播放问题怎么办？',
    a: '如遇播放卡顿，可尝试切换清晰度或更换播放线路。若问题持续，请通过联系我们页面反馈，技术团队会在 24 小时内处理。',
  },
];

export default function FeaturesAndFaq() {
  const [openFaqs, setOpenFaqs] = useState<Set<number>>(new Set([0]));

  const toggleFaq = (idx: number) => {
    const next = new Set(openFaqs);
    if (next.has(idx)) {
      next.delete(idx);
    } else {
      next.add(idx);
    }
    setOpenFaqs(next);
  };

  return (
    <>
      <section id="features" className="py-14 md:py-20 bg-background-50">
        <div className="w-full px-4 md:px-6">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-10 md:mb-12">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
                本站特色
              </h2>
              <p className="text-sm md:text-base text-foreground-600 max-w-xl mx-auto">
                午马影院的核心优势，用心打磨每一个观影细节
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
              {features.map((feat, idx) => (
                <div
                  key={idx}
                  className="text-center p-6 md:p-8 rounded-xl bg-background-100 border border-background-200/70"
                >
                  <div className="w-14 h-14 md:w-16 md:h-16 mx-auto mb-4 flex items-center justify-center rounded-2xl bg-primary-100">
                    <i className={`${feat.icon} text-2xl md:text-3xl text-primary-600`}></i>
                  </div>
                  <h3 className="text-lg font-heading font-semibold text-foreground-900 mb-2">
                    {feat.title}
                  </h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    {feat.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section id="faq" className="py-14 md:py-20 bg-background-100">
        <div className="w-full px-4 md:px-6">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-10 md:mb-12">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
                常见问题
              </h2>
              <p className="text-sm md:text-base text-foreground-600 max-w-xl mx-auto">
                关于午马影院的常见疑问，这里都有答案
              </p>
            </div>
            <div className="max-w-3xl mx-auto space-y-3">
              {faqs.map((faq, idx) => (
                <div
                  key={idx}
                  className="rounded-xl bg-background-50 border border-background-200/70 overflow-hidden"
                >
                  <button
                    onClick={() => toggleFaq(idx)}
                    className="w-full flex items-center justify-between p-4 md:p-5 text-left cursor-pointer"
                  >
                    <h3 className="text-sm md:text-base font-heading font-semibold text-foreground-900 pr-4">
                      {faq.q}
                    </h3>
                    <i
                      className={`flex-shrink-0 text-foreground-500 transition-transform duration-200 ${
                        openFaqs.has(idx) ? 'ri-subtract-line' : 'ri-add-line'
                      }`}
                    ></i>
                  </button>
                  <div
                    className={`px-4 md:px-5 overflow-hidden transition-all duration-300 ${
                      openFaqs.has(idx) ? 'pb-4 md:pb-5 max-h-96' : 'max-h-0'
                    }`}
                  >
                    <p className="text-sm text-foreground-600 leading-relaxed">
                      {faq.a}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}